import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../App';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RefreshCw, Loader2, AlertTriangle, ThumbsUp, ThumbsDown, Minus, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const PLATFORM_COLORS = {
  facebook: { bg: '#1877F2', label: 'Facebook' },
  instagram: { bg: '#E4405F', label: 'Instagram' },
  linkedin: { bg: '#0A66C2', label: 'LinkedIn' },
  tiktok: { bg: '#000000', label: 'TikTok' },
  youtube: { bg: '#FF0000', label: 'YouTube' },
};

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function SentimentPage() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [clientFilter, setClientFilter] = useState('all');

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const params = clientFilter !== 'all' ? `?client_name=${clientFilter}` : '';
      const data = await api(`/api/sentiment/summary${params}`);
      setSummary(data);
    } catch (err) {
      toast.error('Failed to load sentiment data');
    } finally {
      setLoading(false);
    }
  }, [clientFilter]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const handleSync = async () => {
    setSyncing(true);
    toast.info('Sprout is cancelled. Sentiment refresh awaits the replacement connection.');
    try {
      const r = await api('/api/sentiment/sync', { method: 'POST' });
      toast.success(`Synced: ${r.counts?.positive || 0} positive, ${r.counts?.negative || 0} negative. ${r.new_negatives || 0} new alerts sent.`);
      fetchSummary();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSyncing(false);
    }
  };

  const total = summary?.total || 0;
  const positive = summary?.positive || 0;
  const negative = summary?.negative || 0;
  const neutral = summary?.neutral || 0;

  return (
    <div className="animate-fade-in-up">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Sentiment Analysis
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Comment sentiment across all clients — negative comments trigger email alerts
          </p>
        </div>
        <Button onClick={handleSync} disabled={syncing} style={{ background: '#1f2a4d' }}>
          <RefreshCw size={14} className={`mr-2 ${syncing ? 'animate-spin' : ''}`} />
          {syncing ? 'Syncing...' : 'Sync Sentiment'}
        </Button>
      </div>

      {/* Filter */}
      <div className="mb-6">
        <Select value={clientFilter} onValueChange={setClientFilter}>
          <SelectTrigger className="w-[200px]"><SelectValue placeholder="All Clients" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Clients</SelectItem>
            {summary?.recent_negatives && [...new Set(summary.recent_negatives.map(n => n.client_name))].sort().map(c => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="py-16 text-center"><Loader2 className="animate-spin mx-auto" /></div>
      ) : (
        <>
          {/* Sentiment Overview */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardContent className="p-5">
                <div className="text-xs text-muted-foreground uppercase tracking-wider">Total Analyzed</div>
                <div className="text-3xl font-semibold mt-1" style={{ color: '#1f2a4d' }}>{total.toLocaleString()}</div>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-emerald-500">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 text-xs text-emerald-700 uppercase tracking-wider">
                  <ThumbsUp size={12} /> Positive
                </div>
                <div className="text-3xl font-semibold mt-1 text-emerald-700">{positive.toLocaleString()}</div>
                <div className="text-sm text-muted-foreground">{summary?.positive_pct || 0}%</div>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-slate-400">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 text-xs text-slate-600 uppercase tracking-wider">
                  <Minus size={12} /> Neutral
                </div>
                <div className="text-3xl font-semibold mt-1 text-slate-600">{neutral.toLocaleString()}</div>
                <div className="text-sm text-muted-foreground">{summary?.neutral_pct || 0}%</div>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-red-500">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 text-xs text-red-700 uppercase tracking-wider">
                  <ThumbsDown size={12} /> Negative
                </div>
                <div className="text-3xl font-semibold mt-1 text-red-700">{negative.toLocaleString()}</div>
                <div className="text-sm text-muted-foreground">{summary?.negative_pct || 0}%</div>
              </CardContent>
            </Card>
          </div>

          {/* Sentiment Bar */}
          {total > 0 && (
            <Card className="mb-6">
              <CardContent className="p-4">
                <div className="flex rounded-full overflow-hidden h-4">
                  <div style={{ width: `${summary?.positive_pct || 0}%` }} className="bg-emerald-500" title={`Positive: ${summary?.positive_pct}%`} />
                  <div style={{ width: `${summary?.neutral_pct || 0}%` }} className="bg-slate-300" title={`Neutral: ${summary?.neutral_pct}%`} />
                  <div style={{ width: `${summary?.negative_pct || 0}%` }} className="bg-red-500" title={`Negative: ${summary?.negative_pct}%`} />
                </div>
                <div className="flex justify-between mt-2 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Positive {summary?.positive_pct}%</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-slate-300" /> Neutral {summary?.neutral_pct}%</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" /> Negative {summary?.negative_pct}%</span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Recent Negative Comments */}
          <Card>
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-4">
                <AlertTriangle size={16} className="text-red-500" />
                <h3 className="text-sm font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Recent Negative Comments
                </h3>
              </div>
              {(summary?.recent_negatives || []).length === 0 ? (
                <div className="py-8 text-center text-sm text-muted-foreground">
                  No negative comments found. Click "Sync Sentiment" to pull from Sprout.
                </div>
              ) : (
                <div className="divide-y">
                  {(summary?.recent_negatives || []).map((comment) => {
                    const pmeta = PLATFORM_COLORS[comment.platform] || { bg: '#666', label: comment.network || comment.platform };
                    return (
                      <div key={comment.id} className="py-3 flex items-start gap-3">
                        <div className="w-7 h-7 rounded-lg flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                          style={{ background: pmeta.bg }}>
                          {(pmeta.label || '?')[0]}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                            <span className="text-sm font-semibold">{comment.author || 'Unknown'}</span>
                            <Badge variant="outline" className="text-[9px]">{comment.client_name}</Badge>
                            <Badge className="text-[9px] bg-red-100 text-red-700 border-red-200">Negative</Badge>
                            {comment.alerted && <Badge className="text-[9px] bg-amber-100 text-amber-700 border-amber-200">Alerted</Badge>}
                            <span className="text-[10px] text-muted-foreground">{timeAgo(comment.created_time)}</span>
                          </div>
                          <p className="text-sm">{comment.text}</p>
                        </div>
                        {comment.post_url && (
                          <a href={comment.post_url} target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground flex-shrink-0">
                            <ExternalLink size={12} /> View
                          </a>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
